/**
 * Os três grupos da audiometria no cartão do exame (#461, tela 1/2/3 do mockup): Limiares
 * auditivos, Comparação com a referência e Referência.
 *
 * "Salvar limiares" é um botão explícito, fora do salvamento automático da aba (D7). A comparação
 * vem do servidor e só aparece depois de salvar (D6): a tela formata, nunca calcula.
 */

import React, { useState } from 'react';
import { Alert, Box, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';

import ActionButton from '../../../components/common/ActionButton';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useAcao } from '../../../authorization/useVeredito';
import audiometryService from '../../../services/audiometryService';
import { AUDIOGRAMA_ORELHA_DIREITA, AUDIOGRAMA_ORELHA_ESQUERDA } from '../../../theme';
import {
  AUDIOMETRY_FREQUENCIES,
  AUDIOMETRY_MAX_DB,
  AUDIOMETRY_MIN_DB,
  type AudiometryEar,
  type AudiometryRecord,
  type AudiometryThresholds,
  type EarThresholds,
} from '../../../types/audiometry';
import type { AppointmentExam } from '../../../types/appointmentExam';
import type { ContextoDoTrabalhador } from './useContextoDoTrabalhador';
import { descreverPiora, descreverSalvamento, formatarDia, formatarHz } from '../../../utils/audiometria';
import {
  RASCUNHO_VAZIO,
  situacaoDaReferencia,
  type GridDraft,
  type RascunhoDaAudiometria,
} from './rascunhoDaAudiometria';
import { primeiroNome } from '../../employees/components/evolucaoAuditiva';

const PERMISSAO_GRAVAR = 'appointment.update';

const EARS: ReadonlyArray<{ ear: AudiometryEar; label: string; symbol: string; color: string }> = [
  { ear: 'right', label: 'Orelha direita', symbol: '○', color: AUDIOGRAMA_ORELHA_DIREITA },
  { ear: 'left', label: 'Orelha esquerda', symbol: '✕', color: AUDIOGRAMA_ORELHA_ESQUERDA },
];

// ============================================================================
// HELPERS
// ============================================================================

function emptyDraft(): GridDraft {
  return { right: {}, left: {} };
}

function draftFrom(thresholds: AudiometryThresholds | null): GridDraft {
  const draft = emptyDraft();
  if (!thresholds) return draft;
  for (const { ear } of EARS) {
    for (const frequency of AUDIOMETRY_FREQUENCIES) {
      const value = thresholds[ear]?.[`${frequency}`];
      if (value !== null && value !== undefined) draft[ear][`${frequency}`] = String(value);
    }
  }
  return draft;
}

/** `null` quando o texto não é um inteiro dentro da faixa; célula vazia é válida (sem registro). */
function parseCell(text: string): { empty: boolean; value: number | null } {
  const trimmed = text.trim();
  if (trimmed === '') return { empty: true, value: null };
  if (!/^-?\d+$/.test(trimmed)) return { empty: false, value: null };
  const value = Number(trimmed);
  if (value < AUDIOMETRY_MIN_DB || value > AUDIOMETRY_MAX_DB) return { empty: false, value: null };
  return { empty: false, value };
}

function toThresholds(draft: GridDraft): { thresholds: AudiometryThresholds; invalid: boolean; filled: number } {
  const thresholds: AudiometryThresholds = { right: {}, left: {} };
  let invalid = false;
  let filled = 0;
  for (const { ear } of EARS) {
    const target: EarThresholds = thresholds[ear];
    for (const frequency of AUDIOMETRY_FREQUENCIES) {
      const key = `${frequency}` as const;
      const cell = parseCell(draft[ear][key] ?? '');
      if (!cell.empty && cell.value === null) invalid = true;
      if (cell.value !== null) filled += 1;
      target[key] = cell.value;
    }
  }
  return { thresholds, invalid, filled };
}

function formatDelta(value: number | null | undefined, flagged: boolean): string {
  if (value === null || value === undefined) return '—';
  const text = value > 0 ? `+${value}` : value < 0 ? `−${Math.abs(value)}` : '0';
  return flagged ? `▲ ${text}` : text;
}

function errorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { message?: string; error?: string } } } | null)?.response;
  return response?.data?.message ?? response?.data?.error ?? (error instanceof Error ? error.message : fallback);
}

// ============================================================================
// GRADE DE PIORA
// ============================================================================

const GRID_COLUMNS = 'minmax(120px, auto) repeat(8, minmax(48px, 1fr))';

const DeltaGrid: React.FC<{ comparison: NonNullable<AudiometryRecord['comparison']> }> = ({ comparison }) => {
  const flaggedKeys = new Set(comparison.flagged.map((cell) => `${cell.ear}:${cell.frequencyHz}`));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        role="group"
        aria-label="Piora em dB por orelha e frequência"
        sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 0.5, alignItems: 'center', minWidth: 560 }}
      >
        <Box />
        {AUDIOMETRY_FREQUENCIES.map((frequency) => (
          <Typography key={frequency} variant="caption" color="text.secondary" align="center">
            {formatarHz(frequency)}
          </Typography>
        ))}
        {EARS.map(({ ear, label, symbol, color }) => (
          <React.Fragment key={ear}>
            <Typography variant="body2" sx={{ color }}>
              {symbol} {label} <Typography component="span" variant="caption" color="text.secondary">(piora em dB)</Typography>
            </Typography>
            {AUDIOMETRY_FREQUENCIES.map((frequency) => {
              const flagged = flaggedKeys.has(`${ear}:${frequency}`);
              return (
                <Typography
                  key={frequency}
                  variant="body2"
                  align="center"
                  fontWeight={flagged ? 'bold' : 'regular'}
                  data-testid={`piora-${ear}-${frequency}`}
                  sx={{ color: flagged ? 'error.main' : 'text.primary' }}
                >
                  {formatDelta(comparison.delta[ear]?.[`${frequency}`], flagged)}
                </Typography>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// ============================================================================
// COMPONENTE
// ============================================================================

interface AudiometryGroupsProps {
  appointmentId: number;
  exam: AppointmentExam;
  /** Nome, trabalhador e histórico, lidos uma vez pela aba. */
  context: ContextoDoTrabalhador;
  /** Relê o histórico do trabalhador depois que os limiares foram salvos. */
  refreshHistory: (employeeId: number) => Promise<void>;
  /** Rascunho guardado pela aba; sobrevive ao cartão sair de cena. */
  rascunho: RascunhoDaAudiometria;
  onRascunhoChange: (rascunho: RascunhoDaAudiometria) => void;
  onSaved: (record: AudiometryRecord) => void;
}

type PendingConfirm = 'swap' | 'overwrite-reference' | 'reference-unknown' | null;

export const AudiometryGroups: React.FC<AudiometryGroupsProps> = ({
  appointmentId,
  exam,
  context,
  refreshHistory,
  rascunho,
  onRascunhoChange,
  onSaved,
}) => {
  const record = exam.audiometry ?? null;
  const permission = useAcao(PERMISSAO_GRAVAR, 'Salvar limiares');
  const readOnly = permission.disabled;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [raceNotice, setRaceNotice] = useState(false);
  const [confirm, setConfirm] = useState<PendingConfirm>(null);

  const savedThresholds = record?.thresholds ?? null;
  const draft = rascunho.grade ?? draftFrom(savedThresholds);

  const history = context.history;
  const workerName = context.name ?? 'o trabalhador';
  const lostReference = !!history && history.reference !== null && !history.effectiveReference;
  const { isReference, hasEffectiveReference, otherReferenceExists, referenceChecked } = situacaoDaReferencia(
    exam,
    context,
    rascunho,
  );

  const parsed = toThresholds(draft);
  const canSave = !parsed.invalid && parsed.filled > 0 && !saving;

  const setCell = (ear: AudiometryEar, key: string, text: string) => {
    onRascunhoChange({ ...rascunho, grade: { ...draft, [ear]: { ...draft[ear], [key]: text } } });
  };

  const doSave = async () => {
    setConfirm(null);
    setSaving(true);
    setSaveError(null);
    setRaceNotice(false);
    const wantedReference = referenceChecked;
    try {
      const saved = await audiometryService.saveThresholds(appointmentId, exam.id, {
        thresholds: parsed.thresholds,
        ...(history !== null || isReference || wantedReference ? { setAsReference: wantedReference } : {}),
      });
      if (wantedReference && !saved.isReference) setRaceNotice(true);
      onRascunhoChange(RASCUNHO_VAZIO);
      onSaved(saved);
      if (context.employeeId !== null) await refreshHistory(context.employeeId);
    } catch (error) {
      // A grade fica como está: quem digitou 16 valores não os perde por uma recusa.
      setSaveError(errorMessage(error, 'Não foi possível salvar os limiares.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (isReference) setConfirm('overwrite-reference');
    else if (referenceChecked && otherReferenceExists) setConfirm('swap');
    // Sem histórico não se sabe se há outra referência: a troca só segue com a confirmação.
    else if (referenceChecked && history === null) setConfirm('reference-unknown');
    else void doSave();
  };

  const currentReferenceDate = history?.reference ? formatarDia(history.reference.date) : null;
  const comparison = record?.comparison ?? null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2 }}>
      {/* Limiares auditivos */}
      <Box component="section" aria-labelledby={`limiares-${exam.id}`}>
        <Typography id={`limiares-${exam.id}`} variant="subtitle2" fontWeight="bold">
          Limiares auditivos
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Em dB NA, por frequência. Preencha o que o audiograma trouxe; célula vazia fica sem registro.
        </Typography>
        {readOnly && (
          <Alert severity="info" icon={false} sx={{ mb: 1 }}>
            {permission.tooltip}
          </Alert>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            role="group"
            aria-label="Limiares auditivos em dB NA"
            sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 0.5, alignItems: 'center', minWidth: 560 }}
          >
            <Box />
            {AUDIOMETRY_FREQUENCIES.map((frequency) => (
              <Typography key={frequency} variant="caption" color="text.secondary" align="center">
                {formatarHz(frequency)} Hz
              </Typography>
            ))}
            {EARS.map(({ ear, label, symbol, color }) => (
              <React.Fragment key={ear}>
                <Typography variant="body2" sx={{ color }}>
                  {symbol} {label}
                </Typography>
                {AUDIOMETRY_FREQUENCIES.map((frequency) => {
                  const key = `${frequency}`;
                  const text = draft[ear][key] ?? '';
                  const cell = parseCell(text);
                  return (
                    <TextField
                      key={key}
                      size="small"
                      value={text}
                      disabled={readOnly || saving}
                      error={!cell.empty && cell.value === null}
                      onChange={(e) => setCell(ear, key, e.target.value)}
                      inputProps={{
                        inputMode: 'numeric',
                        'aria-label': `${label}, ${formatarHz(frequency)} Hz`,
                        style: { textAlign: 'center', padding: '6px 4px' },
                      }}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        {parsed.invalid && (
          <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 0.5 }}>
            Use números inteiros entre {AUDIOMETRY_MIN_DB} e {AUDIOMETRY_MAX_DB} dB.
          </Typography>
        )}
      </Box>

      {/* Comparação com a referência */}
      <Box component="section" aria-labelledby={`comparacao-${exam.id}`}>
        <Typography id={`comparacao-${exam.id}`} variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
          Comparação com a referência
        </Typography>
        {lostReference && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            O exame que era a referência de {workerName} deixou de estar concluído, e o trabalhador está sem
            referência. Marque a caixa abaixo para usar este exame como a nova referência.
          </Alert>
        )}
        {isReference ? (
          <Typography variant="body2">
            Este exame é a referência de {workerName}: as próximas audiometrias serão comparadas com ele.
          </Typography>
        ) : comparison ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Referência deste trabalhador: audiometria de {formatarDia(comparison.referenceDate)}. O sistema calcula;
              você não digita nada aqui.
            </Typography>
            {comparison.status === 'WORSENED' && (
              <>
                <Typography variant="body1" fontWeight="bold">
                  Houve piora.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {descreverPiora(comparison.flagged)} Sugerimos revisar o resultado acima e registrar a conduta
                  nos detalhes.
                </Typography>
              </>
            )}
            {comparison.status === 'STABLE' && (
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                Sem piora em relação à referência.
              </Typography>
            )}
            {comparison.status === 'NOT_COMPARABLE' && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Nenhuma frequência de 3.000, 4.000 ou 6.000 Hz tem valor nos dois exames; não há como comparar.
              </Typography>
            )}
            {comparison.status === 'BEFORE_REFERENCE' && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Este exame é anterior à referência e não é comparado com ela.
              </Typography>
            )}
            {(comparison.status === 'WORSENED' || comparison.status === 'STABLE') && (
              <>
                <DeltaGrid comparison={comparison} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ▲ = piora de 15 dB ou mais em 3.000, 4.000 ou 6.000 Hz.
                </Typography>
              </>
            )}
          </>
        ) : savedThresholds && !lostReference ? (
          <Typography variant="body2" color="text.secondary">
            Sem referência para comparar: {workerName} ainda não tem uma audiometria de referência.
          </Typography>
        ) : !savedThresholds && history !== null && !hasEffectiveReference && !lostReference ? (
          <Typography variant="body2">
            Esta é a primeira audiometria de {workerName} no sistema. Ela será a <strong>referência</strong>: as
            próximas audiometrias dele serão comparadas com ela.
          </Typography>
        ) : !savedThresholds ? (
          <Typography variant="body2" color="text.secondary">
            Atualiza quando você salvar os limiares
          </Typography>
        ) : null}
      </Box>

      {/* Referência */}
      <Box component="section" aria-labelledby={`referencia-${exam.id}`}>
        <Typography id={`referencia-${exam.id}`} variant="subtitle2" fontWeight="bold">
          Referência
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={referenceChecked}
              disabled={readOnly || saving || isReference}
              onChange={(e) => onRascunhoChange({ ...rascunho, referenceChoice: e.target.checked })}
            />
          }
          label="Usar este exame como a nova referência do trabalhador"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {isReference
            ? 'Este exame já é a referência. Para trocar, marque outro exame como referência.'
            : otherReferenceExists && currentReferenceDate
              ? `Hoje a referência é a audiometria de ${currentReferenceDate}. Troque só quando este exame passar a ser o ponto de partida das próximas comparações.`
              : !hasEffectiveReference && history !== null
                ? 'Já marcado porque ainda não existe outra referência. Desmarque se este exame não puder servir de ponto de partida.'
                : 'Troque só quando este exame passar a ser o ponto de partida das próximas comparações.'}
        </Typography>

        {raceNotice && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Este trabalhador já tem referência: seus limiares foram salvos, mas este exame não virou a referência.
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {saveError} Os valores digitados continuam na grade.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
          <ActionButton
            acao={PERMISSAO_GRAVAR}
            rotulo="Salvar limiares"
            variant="contained"
            size="small"
            disabled={!canSave}
            onClick={handleSaveClick}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Salvar limiares
          </ActionButton>
          <Typography variant="caption" color="text.secondary">
            {savedThresholds ? descreverSalvamento(record?.savedAt, record?.savedBy) : 'Ainda não salvo'}
          </Typography>
        </Box>
      </Box>

      <ConfirmDialog
        open={confirm === 'swap'}
        title={`Trocar a referência de ${primeiroNome(context.name ?? undefined) ?? 'o trabalhador'}?`}
        message={`Hoje a referência dele é a audiometria de ${currentReferenceDate ?? 'outra data'}. Se você usar a de ${formatarDia(context.appointmentDate)}, as próximas comparações partem dela. A audiometria anterior continua guardada, só deixa de ser o ponto de comparação. Você pode voltar atrás a qualquer momento.`}
        confirmLabel="Trocar a referência"
        confirmColor="primary"
        cancelLabel="Manter a referência atual"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'reference-unknown'}
        title="Usar este exame como referência?"
        message="Se este trabalhador já tiver outra referência, ela será trocada e as próximas comparações partirão deste exame."
        confirmLabel="Usar como referência"
        confirmColor="primary"
        cancelLabel="Voltar"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'overwrite-reference'}
        title="Salvar limiares da referência?"
        message={`Este exame é a referência de ${workerName}. Mudar os valores muda todas as comparações dele. Salvar mesmo assim?`}
        confirmLabel="Salvar mesmo assim"
        confirmColor="primary"
        cancelLabel="Voltar"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
};

export default AudiometryGroups;
